import dayjs from "dayjs";
import { EmployeeShiftRecord } from "../services/model/EmployeeShiftRecord";

/**
 * Formats a Date object to a HH:MM string (24-hour, zero-padded).
 */
export const formatTimeHHMM = (date: Date): string => {
  const h = String(date.getHours()).padStart(2, "0");
  const m = String(date.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
};

/**
 * Builds an ISO timestamp string from a HH:MM time value using today's date and local offset.
 */
export const buildIsoTimestamp = (time: string): string => {
  const date = dayjs().format("YYYY-MM-DD");
  const offset = dayjs().format("Z");
  return `${date}T${time}:00${offset}`;
};

export enum LateCategory {
  ON_TIME = "on_time",
  LATE_MINOR = "late_minor",
  LATE_MAJOR = "late_major",
  NOT_CLOCKED_IN = "not_clocked_in",
}

/**
 * Color constants for late categories - used by timeline and legend components
 */
export const LATE_CATEGORY_COLORS: Record<LateCategory, string> = {
  on_time: "#062133",
  late_minor: "#FFA500",
  late_major: "#FF6347",
  not_clocked_in: "#9CA3AF",
} as const;

/**
 * Determines the late category for color coding
 * Accepts either just late minutes (including null), or a full record for status-aware categorization
 */
export const getLateCategory = (
  lateMinutesOrRecord: number | null | EmployeeShiftRecord,
  thresholdMinutes = 0
): LateCategory => {
  // When late_minutes is null (employee not yet clocked in or data unavailable),
  // default to ON_TIME — no late category can be determined.
  if (lateMinutesOrRecord === null) {
    return LateCategory.ON_TIME;
  }

  // Handle record-based categorization
  if (typeof lateMinutesOrRecord === "object") {
    const record = lateMinutesOrRecord;
    if (record.status === "not_clocked_in" || record.status === "no_show") {
      return LateCategory.NOT_CLOCKED_IN;
    }
    return getLateCategory(record.late_minutes, thresholdMinutes);
  }

  // Handle minutes-based categorization
  const lateMinutes = lateMinutesOrRecord;
  // Treat as on-time if within threshold (or early/zero)
  if (lateMinutes <= thresholdMinutes) {
    return LateCategory.ON_TIME;
  }
  if (lateMinutes <= thresholdMinutes + 15) {
    return LateCategory.LATE_MINOR;
  }
  return LateCategory.LATE_MAJOR;
};

/**
 * Parses a time string to minutes from midnight
 * @param time - Time string in HH:mm or HH:mm:ss format (e.g., "08:30", "23:45:00")
 * @returns Minutes from midnight, or null if input is invalid
 */
export const parseTimeToMinutes = (time: string): number | null => {
  // Validate input is a non-empty string
  if (!time || typeof time !== "string") {
    return null;
  }

  // Validate HH:MM, HH:MM:SS, or HH:MM:SS.microseconds format using regex
  const timePattern = /^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/;
  const match = time.match(timePattern);

  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);

  // Verify parsed values are finite numbers
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return null;
  }

  // Validate hours (0-23) and minutes (0-59) ranges
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }

  return hours * 60 + minutes;
};

/**
 * Pads a HH:MM time by a given number of minutes, wrapping around 24h.
 */
export const padTime = (time: string, offsetMinutes: number): string => {
  const parsed = parseTimeToMinutes(time);
  if (parsed === null) return time;
  const total = (((parsed + offsetMinutes) % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
};

/**
 * Calculates the timeline position as a percentage between start and end
 * Handles overnight shifts where end time is before start time
 */
export const getTimelinePosition = (
  time: string,
  shiftStart: string,
  shiftEnd: string
): number => {
  const parsedTime = parseTimeToMinutes(time);
  const startMinutes = parseTimeToMinutes(shiftStart);
  const parsedEnd = parseTimeToMinutes(shiftEnd);

  // Return 0 if any time values are invalid
  if (parsedTime === null || startMinutes === null || parsedEnd === null) {
    return 0;
  }

  let timeMinutes = parsedTime;
  let endMinutes = parsedEnd;

  // Detect overnight shift first (e.g., 22:00 - 06:00)
  const isOvernight = endMinutes <= startMinutes;

  // Only adjust for overnight shifts
  if (isOvernight) {
    endMinutes += 24 * 60;

    // Only roll time forward if it's an overnight shift AND time is before start
    // (meaning the time is in the early morning portion of the overnight shift)
    if (timeMinutes < startMinutes) {
      timeMinutes += 24 * 60;
    }
  }

  const totalDuration = endMinutes - startMinutes;

  // Avoid division by zero
  if (totalDuration === 0) {
    return 0;
  }

  const position = timeMinutes - startMinutes;
  const percent = (position / totalDuration) * 100;

  return Math.max(0, Math.min(100, percent));
};

/**
 * Determines the shift label based on start and end times
 * Returns "Day Shift" as safe default for invalid inputs
 */
export const getShiftLabel = (shiftStart: string, shiftEnd: string): string => {
  // Validate inputs using parseTimeToMinutes
  const startMinutes = parseTimeToMinutes(shiftStart);
  const endMinutes = parseTimeToMinutes(shiftEnd);

  // Return safe default if inputs are invalid
  if (startMinutes === null || endMinutes === null) {
    return "Day Shift";
  }

  const startHour = Math.floor(startMinutes / 60);
  const endHour = Math.floor(endMinutes / 60);

  // Night shift: crosses midnight (end < start) or starts late evening (22:00+)
  // Note: early morning same-day shifts (e.g., 04:00-06:00) are NOT night shifts
  if (endHour < startHour || startHour >= 22) {
    return "Night Shift";
  }

  // Evening shift: starts between 14:00 and 22:00
  if (startHour >= 14 && startHour < 22) {
    return "Evening Shift";
  }

  // Day shift: starts before 14:00 (including early morning)
  return "Day Shift";
};

/**
 * Formats a time string for display (e.g., "08:00" -> "8am")
 */
export const formatTimeShort = (time: string): string => {
  if (!time || typeof time !== "string" || !time.includes(":")) return "";
  const parts = time.split(":");
  const hours = Number(parts[0]);
  const minutes = Number(parts[1]);
  if (!Number.isFinite(hours) || hours < 0 || hours > 23) return "";
  if (!Number.isFinite(minutes) || minutes < 0 || minutes > 59) return "";
  const period = hours >= 12 ? "pm" : "am";
  let displayHours = hours;
  if (hours === 0) displayHours = 12;
  else if (hours > 12) displayHours = hours - 12;
  const minStr = minutes.toString().padStart(2, "0");
  return `${displayHours}:${minStr}${period}`;
};

/**
 * Generates hour markers for a timeline
 * Handles overnight shifts where end time is before start time
 * Returns empty array for invalid inputs
 */
export const generateHourMarkers = (
  shiftStart: string,
  shiftEnd: string
): string[] => {
  // Validate inputs using parseTimeToMinutes
  const startMinutes = parseTimeToMinutes(shiftStart);
  const endMinutes = parseTimeToMinutes(shiftEnd);

  // Return empty array if inputs are invalid
  if (startMinutes === null || endMinutes === null) {
    return [];
  }

  const startHour = Math.floor(startMinutes / 60);
  let endHour = Math.floor(endMinutes / 60);
  const markers: string[] = [];

  // Handle overnight shifts (e.g., 22:00 - 06:00)
  if (endHour < startHour) {
    endHour += 24;
  }

  for (let hour = startHour; hour <= endHour; hour += 1) {
    // Wrap hours back to 0-23 range
    const displayHour = hour % 24;
    markers.push(`${displayHour.toString().padStart(2, "0")}:00`);
  }

  return markers;
};

/**
 * Returns the status badge variant and label for an employee record
 * @param options.showMinutes - If true, shows late minutes in label (e.g., "Late (5m)")
 */
/**
 * Check if a shift is currently active or starting within a buffer (default 1 hour).
 * Handles overnight shifts (e.g., 18:00-06:00).
 */
export const isShiftActive = (
  shiftStart: string | null | undefined,
  shiftEnd: string | null | undefined,
  bufferMinutes = 60
): boolean => {
  const startMin = shiftStart ? parseTimeToMinutes(shiftStart) : null;
  const endMin = shiftEnd ? parseTimeToMinutes(shiftEnd) : null;
  if (startMin === null || endMin === null) return false;
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const isOvernight = endMin <= startMin;

  if (isOvernight) {
    return nowMin >= startMin - bufferMinutes || nowMin < endMin;
  }
  return nowMin >= startMin - bufferMinutes && nowMin < endMin + bufferMinutes;
};

/**
 * Find the current active shift config from a list (no buffer — exact match).
 * Returns the first active config, or the first config as fallback.
 */
export const getActiveShiftConfig = (
  configs: { id: string; start_time: string | null; end_time: string | null }[]
): string | null => {
  const active = configs.find(
    (c) =>
      c.start_time && c.end_time && isShiftActive(c.start_time, c.end_time, 0)
  );
  return active?.id || configs[0]?.id || null;
};

/**
 * Find the currently-active shift NAME from a list of configs. Unlike
 * `getActiveShiftConfig` this does not fall back to the first config when
 * nothing is active — returns null so callers can fall back to top-level
 * totals in that case.
 */
export const getActiveShiftName = (
  configs:
    | { name: string; start_time: string | null; end_time: string | null }[]
    | undefined
    | null
): string | null => {
  if (!configs || configs.length === 0) return null;
  const active = configs.find(
    (c) =>
      c.start_time && c.end_time && isShiftActive(c.start_time, c.end_time, 0)
  );
  return active?.name ?? null;
};

/**
 * Whether an attendance record should count toward the headcount shown.
 *
 * Live ("today") view: only employees physically on site now — clocked in
 * with no matching clock-out. Historical view: everyone who attended that
 * day — i.e. clocked in at all, whether or not they later clocked out. For a
 * finished day almost everyone has clocked out, so the live definition would
 * collapse the count to forgotten-clock-out / ghost rows and badly undercount
 * real attendance. No-shows have no clock-in and are naturally excluded.
 */
export const isOnSiteRecord = (
  record: { clock_in_time: string | null; clock_out_time: string | null },
  isTodayView: boolean
): boolean =>
  isTodayView
    ? Boolean(record.clock_in_time && !record.clock_out_time)
    : Boolean(record.clock_in_time);

type CountableRecord = Pick<
  EmployeeShiftRecord,
  | "clock_in_time"
  | "clock_out_time"
  | "is_replacement"
  | "is_reliever"
  | "is_unscheduled"
>;

/** An off-roster reliever row (backend ``is_reliever`` flag). */
export const isRelieverRecord = (r: CountableRecord): boolean =>
  r.is_reliever === true;

/**
 * An extra body that can cover an absent scheduled slot — a formal replacement,
 * an off-roster reliever, or an ad-hoc unscheduled clock-in. None of these
 * occupy a scheduled slot themselves, so they're excluded from the denominator.
 */
export const isCoveringRecord = (r: CountableRecord): boolean =>
  r.is_replacement === true || isRelieverRecord(r) || r.is_unscheduled === true;

/** A row occupying a scheduled roster slot — the X/Y denominator. */
export const isScheduledRecord = (r: CountableRecord): boolean =>
  !isCoveringRecord(r);

/** Anyone who clocked in at all (attended), regardless of clock-out. */
export const hasAttended = (r: { clock_in_time: string | null }): boolean =>
  Boolean(r.clock_in_time);

export interface SiteAttendanceCounts {
  /** Scheduled-only rows — the X/Y denominator. */
  scheduled: number;
  /** Scheduled slots filled by anyone who attended (scheduled, replacement, or
   *  reliever), capped at ``scheduled``. The "Covered" numerator. */
  covered: number;
  /** Open clock-ins across all rows — bodies physically on site right now. */
  presentNow: number;
  /** Any clock-in across all rows — bodies who attended that day. */
  attended: number;
}

/**
 * Coverage-semantics counts for a set of attendance records. A scheduled slot
 * counts as covered when its own person attended OR a covering body (replacement
 * / reliever) attended — so a no-show whose slot a reliever filled still reads as
 * covered. ``covered`` is clamped to ``scheduled`` so extra bodies can't push it
 * past 100%. A covered no-show (``is_replaced``, no clock-in) stays a scheduled
 * row and isn't double-counted — its covering partner supplies the attendance.
 */
export const computeAttendanceCounts = (
  records: CountableRecord[]
): SiteAttendanceCounts => {
  let scheduled = 0;
  let scheduledAttended = 0;
  let coveringAttended = 0;
  let presentNow = 0;
  let attended = 0;
  records.forEach((r) => {
    const att = hasAttended(r);
    if (isScheduledRecord(r)) {
      scheduled += 1;
      if (att) scheduledAttended += 1;
    } else if (att) {
      coveringAttended += 1;
    }
    if (isOnSiteRecord(r, true)) presentNow += 1;
    if (att) attended += 1;
  });
  return {
    scheduled,
    covered: Math.min(scheduledAttended + coveringAttended, scheduled),
    presentNow,
    attended,
  };
};

/**
 * Check if a shift has ended, handling overnight shifts (e.g., 18:00-06:00).
 */
export const hasShiftEnded = (
  shiftStart: string,
  shiftEnd: string
): boolean => {
  const [startH, startM] = shiftStart.split(":").map(Number);
  const [endH, endM] = shiftEnd.split(":").map(Number);
  const startMin = startH * 60 + startM;
  const endMin = endH * 60 + endM;
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const isOvernight = endMin <= startMin;

  if (isOvernight) {
    // Overnight shift (e.g., 18:00-06:00):
    // Only consider ended within a reasonable window after end time.
    // Beyond that, the next shift cycle hasn't started yet.
    // e.g., for 18:00-06:00 at 08:00 → ended (2h after end) ✓
    //        for 18:00-06:00 at 11:30 → not ended (shift starts tonight) ✓
    const maxWindow = endMin + 3 * 60; // 3 hours after shift end
    return nowMin >= endMin && nowMin < Math.min(maxWindow, startMin);
  }
  return nowMin >= endMin;
};

export const getStatusBadge = (
  record: EmployeeShiftRecord,
  options: { showMinutes?: boolean; thresholdMinutes?: number } = {}
): { variant: "success" | "warning" | "error" | "neutral"; label: string } => {
  // Backend-confirmed no_show always renders as "No Show" regardless of shift timing
  if (record.status === "no_show") {
    return { variant: "error", label: "No Show" };
  }
  const category = getLateCategory(record, options.thresholdMinutes);
  switch (category) {
    case LateCategory.ON_TIME:
      return { variant: "success", label: "On Time" };
    case LateCategory.LATE_MINOR:
      return {
        variant: "warning",
        label: options.showMinutes
          ? `Late (${record.late_minutes ?? 0}m)`
          : "Late Arrival",
      };
    case LateCategory.LATE_MAJOR:
      return {
        variant: "error",
        label: options.showMinutes
          ? `Late (${record.late_minutes ?? 0}m)`
          : "Late Arrival",
      };
    case LateCategory.NOT_CLOCKED_IN: {
      const shiftEnded = hasShiftEnded(record.shift_start, record.shift_end);
      if (shiftEnded) {
        return { variant: "error", label: "No Show" };
      }
      return { variant: "neutral", label: "No Data" };
    }
    default:
      return { variant: "neutral", label: "" };
  }
};

/**
 * Computes actual hours worked from clock-in and clock-out times
 * Returns null if either time is missing
 */
export const computeActualHours = (
  clockIn: string | null,
  clockOut: string | null
): number | null => {
  if (!clockIn || !clockOut) return null;
  const inMin = parseTimeToMinutes(clockIn);
  const outMin = parseTimeToMinutes(clockOut);
  if (inMin === null || outMin === null) return null;

  const diff = outMin - inMin;

  // Handle overnight shifts (negative diff)
  if (diff < 0) {
    const overnightDiff = diff + 24 * 60;

    // Validate overnight shift duration (must be positive and <= 16 hours)
    if (overnightDiff <= 0 || overnightDiff > 16 * 60) {
      // eslint-disable-next-line no-console
      console.warn(
        `Invalid overnight shift duration: inMin=${inMin}, outMin=${outMin}, diff=${diff}, overnightDiff=${overnightDiff}`
      );
      return null;
    }

    return Math.round((overnightDiff / 60) * 10) / 10;
  }

  return Math.round((diff / 60) * 10) / 10;
};

/**
 * Returns the display label for an override source
 * e.g., "admin" -> "by Admin", "supervisor" -> "by Supervisor"
 */
export const getOverrideSourceLabel = (
  source: string | null | undefined
): string | null => {
  if (!source || source === "employee") return null;
  if (source === "admin") return "by Admin";
  if (source === "supervisor") return "by Supervisor";
  return null;
};

/**
 * Returns the tooltip text for an override, combining source label and reason
 */
export const getOverrideTooltipText = (
  overrideBy: string | null | undefined,
  reason: string | null | undefined
): string | null => {
  if (!overrideBy && !reason) return null;
  const parts: string[] = [];
  if (overrideBy) parts.push(`Override by: ${overrideBy}`);
  if (reason) parts.push(`Reason: ${reason}`);
  return parts.join("\n");
};
